import { useEffect, useState, type FormEvent } from "react";
import {
  activities as api,
  type ActivityKind,
  type InvestorActivity,
} from "../../../api";
import {
  formatDateTime,
  fromLocalDateTimeInput,
  toLocalDateTimeInput,
} from "../format";

const KIND_META: Record<ActivityKind, { label: string; glyph: string }> = {
  call: { label: "Call", glyph: "☏" },
  meeting: { label: "Meeting", glyph: "◇" },
  email: { label: "Email", glyph: "✉" },
};

export function InvestorActivity({ investorId }: { investorId: number }) {
  const [items, setItems] = useState<InvestorActivity[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<
    | { mode: "create" }
    | { mode: "edit"; activity: InvestorActivity }
    | null
  >(null);
  const [pendingDelete, setPendingDelete] = useState<InvestorActivity | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api
      .list(investorId)
      .then((d) => {
        if (!cancelled) setItems(d.activities);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [investorId, reloadCount]);

  const reload = () => setReloadCount((n) => n + 1);

  async function handleDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api.remove(investorId, pendingDelete.id);
      setPendingDelete(null);
      reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <section>
      <div className="tab-toolbar">
        <div className="muted">Calls, meetings, and emails for this investor.</div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setEditing({ mode: "create" })}
        >
          + Log activity
        </button>
      </div>

      {error && <div className="banner-error">{error}</div>}

      {items === null ? (
        <div className="muted">Loading…</div>
      ) : items.length === 0 ? (
        <div className="empty">
          <p>No activity logged yet.</p>
        </div>
      ) : (
        <ol className="timeline">
          {items.map((a) => (
            <li key={a.id} className="timeline-item">
              <div className={`timeline-glyph kind-${a.kind}`}>
                {KIND_META[a.kind].glyph}
              </div>
              <div className="timeline-body">
                <div className="timeline-head">
                  <div>
                    <span className={`pill kind-${a.kind}`}>
                      {KIND_META[a.kind].label}
                    </span>
                    <span className="timeline-date">
                      {formatDateTime(a.occurred_at)}
                    </span>
                  </div>
                  <div className="timeline-actions">
                    <button
                      type="button"
                      className="link-action"
                      onClick={() => setEditing({ mode: "edit", activity: a })}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="link-action danger"
                      onClick={() => setPendingDelete(a)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <h3 className="timeline-subject">{a.subject}</h3>
                {a.notes && <p className="timeline-notes">{a.notes}</p>}
              </div>
            </li>
          ))}
        </ol>
      )}

      {editing && (
        <ActivityForm
          investorId={investorId}
          initial={editing.mode === "edit" ? editing.activity : null}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}

      {pendingDelete && (
        <div className="modal-backdrop" onClick={() => setPendingDelete(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Delete activity?</h2>
            <p>
              <strong>{pendingDelete.subject}</strong> will be removed.
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setPendingDelete(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function ActivityForm({
  investorId,
  initial,
  onClose,
  onSaved,
}: {
  investorId: number;
  initial: InvestorActivity | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [kind, setKind] = useState<ActivityKind>(initial?.kind ?? "meeting");
  const [subject, setSubject] = useState(initial?.subject ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [occurredAt, setOccurredAt] = useState(
    initial
      ? toLocalDateTimeInput(initial.occurred_at)
      : toLocalDateTimeInput(new Date().toISOString()),
  );
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!subject.trim()) {
      setError("Subject is required.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        kind,
        subject: subject.trim(),
        notes: notes.trim() || null,
        occurred_at: fromLocalDateTimeInput(occurredAt),
      };
      if (initial) await api.update(investorId, initial.id, payload);
      else await api.create(investorId, payload);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2>{initial ? "Edit activity" : "Log activity"}</h2>
        <form onSubmit={onSubmit} className="card-form" noValidate>
          <div className="kind-picker">
            {(Object.keys(KIND_META) as ActivityKind[]).map((k) => (
              <button
                key={k}
                type="button"
                className={`kind-option ${kind === k ? "active" : ""}`}
                onClick={() => setKind(k)}
              >
                <span className="glyph">{KIND_META[k].glyph}</span>
                {KIND_META[k].label}
              </button>
            ))}
          </div>

          <label className="field">
            <span>Subject *</span>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              autoFocus
              required
            />
          </label>

          <label className="field">
            <span>When</span>
            <input
              type="datetime-local"
              value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
            />
          </label>

          <label className="field">
            <span>Notes</span>
            <textarea
              rows={4}
              value={notes ?? ""}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>

          {error && <div className="banner-error">{error}</div>}

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting ? "Saving…" : initial ? "Save changes" : "Log activity"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
